import React from "react";
import { Link } from "react-router-dom";
import { Compass } from "lucide-react";

const PORTRAIT_IMG = "https://customer-assets.emergentagent.com/job_voyage-ia-1/artifacts/r63gfox9_D43F3286-FA32-42C1-A172-FF2F5D6FEF1E.JPEG";
const JOURNAL_IMG = "https://customer-assets.emergentagent.com/job_voyage-ia-1/artifacts/zz1pwu2u_MVIMG_20251017_060752.jpg";

const timeline = [
  { year: "2004", place: "Bénin", flag: "🌍", color: "#C17F59", desc: "3 ans de pistes africaines et d'horizons infinis." },
  { year: "2007", place: "La Réunion", flag: "🌋", color: "#8B5E34", desc: "Retour en France puis l'île intense — volcans, lagon, forêts." },
  { year: "2010", place: "France", flag: "🏰", color: "#5C554E", desc: "5 ans d'ancrage métropolitain, mais l'horizon tire toujours." },
  { year: "2015", place: "Maroc", flag: "🏜️", color: "#C17F59", desc: "1 an entre déserts brûlants, médinas et hospitalité berbère." },
  { year: "2016", place: "France", flag: "🌿", color: "#5C554E", desc: "6 ans pour distiller les souvenirs et préparer la suite." },
  { year: "2022", place: "Madagascar", flag: "🦎", color: "#8B5E34", desc: "1 an sur la Grande Île — baobabs, lémuriens, pistes de latérite." },
  { year: "2024", place: "France", flag: "📝", color: "#5C554E", desc: "2 ans pour consolider l'expérience." },
  { year: "2025", place: "La Réunion", flag: "🌺", color: "#C17F59", desc: "Retour à l'île-passion. L'Océan Indien comme port d'attache." },
  { year: "2026", place: "Voyage-r", flag: "🧭", color: "#C17F59", isLast: true, desc: "Je lance Voyage-r — et je mets toute une vie d'exploration au service de vos aventures." },
];

export default function AboutPage() {
  return (
    <div className="min-h-screen" style={{ background: "#F9F6F0" }}>
      <nav
        className="sticky top-0 z-50 border-b border-[#DCD4C6]"
        style={{ backdropFilter: "blur(16px)", background: "rgba(249,246,240,0.9)" }}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontStyle: "italic", fontWeight: 600, fontSize: "1.5rem", color: "#2B2623", textDecoration: "none" }} data-testid="about-logo">
            <div className="w-8 h-8 rounded-full border-2 border-[#C17F59] flex items-center justify-center">
              <Compass size={16} color="#C17F59" strokeWidth={1.5} />
            </div>
            Voyage-r
          </Link>
          <Link to="/" className="text-sm text-[#5C554E] hover:text-[#2B2623] transition-colors" style={{ fontFamily: "'Lora', Georgia, serif" }}>
            Retour
          </Link>
        </div>
      </nav>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-20">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <div className="animate-fade-in-up">
            <p className="section-label mb-4">À propos</p>
            <h1 className="text-5xl sm:text-6xl mb-6 leading-tight" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: "#2B2623" }}>
              Diego,<br /><span className="italic" style={{ color: "#C17F59" }}>né pour l'horizon</span>
            </h1>
            <blockquote className="text-lg text-[#5C554E] mb-8 pl-5 border-l-2 border-[#C17F59] italic leading-relaxed" style={{ fontFamily: "'Lora', Georgia, serif" }}>
              "Tout a commencé les pieds nus sur les pistes africaines. Mes parents n'étaient pas de ceux que l'arrivée d'un enfant surprise allait arrêter de faire voyager. J'ai grandi avec l'horizon pour seule limite."
            </blockquote>
            <div className="space-y-5 text-[#5C554E] leading-relaxed" style={{ fontFamily: "'Lora', Georgia, serif" }}>
              <p>Spécialisé dans <strong className="text-[#2B2623]">l'Afrique sauvage, l'Océan Indien et l'Europe authentique</strong>, mon rôle est de vous guider entre châteaux séculaires, déserts brûlants et criques secrètes. Mon but ? Vous faire retrouver cette étincelle de l'aventure.</p>
              <p>Que vous partiez avec un sac à dos ou trois valises, que vos nuits se passent sous une tente de toit ou dans le confort d'un resort, l'essentiel reste le même. Car comme le disait Robert Louis Stevenson :</p>
              <blockquote className="text-base italic pl-4 border-l border-[#DCD4C6] text-[#8B5E34]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "1.2rem" }}>
                "L'important, ce n'est pas la destination, c'est le voyage."
              </blockquote>
              <p>Nous sommes, par nature, des explorateurs. Laissez-moi vous aider à tracer votre propre route.</p>
            </div>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link to="/register" className="btn-primary flex items-center gap-2" data-testid="about-cta-btn">
                <Compass size={15} strokeWidth={1.5} /> Planifier mon voyage
              </Link>
              <Link to="/" className="btn-secondary text-sm">Retour à l'accueil</Link>
            </div>
          </div>

          <div className="relative animate-fade-in delay-200">
            <div className="bg-[#F9F6F0] p-4 pb-12 border border-[#DCD4C6] shadow-lg transform -rotate-1 animate-float mb-6">
              <img src={PORTRAIT_IMG} alt="Diego expert voyage" className="w-full h-[340px] object-cover" style={{ objectPosition: "center 65%" }} loading="lazy" />
              <p className="mt-3 text-center text-base text-[#8B5E34]" style={{ fontFamily: "'Caveat', cursive" }}>Quelque part entre deux continents</p>
            </div>
            <div className="hidden sm:block bg-[#F9F6F0] p-3 pb-10 border border-[#DCD4C6] shadow-md transform rotate-2 absolute -bottom-8 -right-4 w-48" style={{ opacity: 0.9 }}>
              <img src={JOURNAL_IMG} alt="Sur le toit de l'Océan Indien" className="w-full h-28 object-cover" style={{ objectPosition: "center 38%" }} loading="lazy" />
              <p className="mt-2 text-xs text-center text-[#8B5E34]" style={{ fontFamily: "'Caveat', cursive" }}>Sur le toit de l'Océan Indien</p>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-[#DCD4C6] py-20" style={{ background: "#F1EBE1" }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-14">
            <p className="section-label mb-3">La carte de vie</p>
            <h2 className="text-4xl sm:text-5xl" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: "#2B2623" }}>
              Une vie tracée sur la carte du monde
            </h2>
          </div>
          <div className="relative">
            <div className="absolute left-6 top-0 bottom-0 w-px bg-[#DCD4C6]" />
            <div className="space-y-0">
              {timeline.map((ev, i) => (
                <div key={i} className="relative pl-16 pb-10 animate-fade-in-up" style={{ animationDelay: `${i * 70}ms` }} data-testid={`timeline-${i}`}>
                  <div className="absolute left-3 top-1 timeline-dot z-10" />
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <span style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, fontSize: "1.5rem", color: "#2B2623" }}>{ev.year}</span>
                      <span style={{ fontFamily: "'Caveat', cursive", fontSize: "1.1rem", color: ev.color }}>{ev.flag} {ev.place}</span>
                    </div>
                    <p className="text-sm text-[#5C554E] leading-relaxed" style={{ fontFamily: "'Lora', Georgia, serif" }}>{ev.desc}</p>
                    {ev.isLast && (
                      <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-[#C17F59] text-[#F9F6F0] text-sm" style={{ borderRadius: "2px", fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600 }}>
                        <Compass size={14} strokeWidth={1.5} /> Naissance de Voyage-r
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 max-w-3xl mx-auto px-5 sm:px-8 text-center">
        <p className="section-label mb-4">Votre tour</p>
        <h2 className="text-4xl sm:text-5xl mb-6" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: "#2B2623" }}>
          Prêt à écrire votre propre histoire ?
        </h2>
        <p className="text-base text-[#5C554E] mb-8 italic leading-relaxed" style={{ fontFamily: "'Lora', Georgia, serif" }}>
          Chaque voyage que je valide, c'est un peu de cette expérience accumulée en 20 ans que je mets au service de votre aventure.
        </p>
        <Link to="/register" className="btn-primary text-base" data-testid="about-bottom-cta">Commencer mon voyage</Link>
      </section>

      <footer className="border-t border-[#DCD4C6] py-8" style={{ background: "#F1EBE1" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 text-[#5C554E]" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontStyle: "italic", fontWeight: 600, fontSize: "1.2rem", textDecoration: "none" }}>
            <div className="w-6 h-6 rounded-full border border-[#C17F59] flex items-center justify-center">
              <Compass size={12} color="#C17F59" strokeWidth={1.5} />
            </div>
            Voyage-r
          </Link>
          <p className="text-xs text-[#9A8F86] italic" style={{ fontFamily: "'Lora', Georgia, serif" }}>Voyage conçu sur mesure · Validé personnellement par Diego</p>
        </div>
      </footer>
    </div>
  );
}
